"use client";
import { useState, useEffect, useCallback } from 'react';
import { useSession } from 'next-auth/react';

export function useSubscription() {
    const { data: session, status } = useSession();
    const [subscription, setSubscription] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingPlans, setIsLoadingPlans] = useState(true);
    const [error, setError] = useState(null);
    const [plansError, setPlansError] = useState(null);

    // Fetch les plans disponibles
    const fetchAvailablePlans = useCallback(async () => {
        try {
            setIsLoadingPlans(true);
            setPlansError(null);

            const response = await fetch(process.env.NEXT_PUBLIC_API_URL + 'subscriptions/available', {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            if (result.status === 'Success' && result.data) {
                setAvailablePlans(result.data);
            } else {
                throw new Error(result.message || 'Erreur lors de la récupération des plans');
            }
        } catch (err) {
            setPlansError(err.message);
            console.error('Erreur fetchAvailablePlans:', err);
        } finally {
            setIsLoadingPlans(false);
        }
    }, []);

    // Fetch l'abonnement actuel
    const fetchSubscription = useCallback(async () => {
        // Ne pas fetch si l'utilisateur n'est pas connecté
        if (status !== 'authenticated') {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            console.log(session);
            const response = await fetch(process.env.NEXT_PUBLIC_API_URL + 'subscriptions/current', {
                headers: {
                    'Authorization': `Bearer ${session?.user?.token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            if (result.status === 'Success' && result.data) {
                setSubscription(result.data);
            } else {
                throw new Error(result.message || 'Erreur lors de la récupération de l\'abonnement');
            }
        } catch (err) {
            setError(err.message);
            console.error('Erreur useSubscription:', err);
        } finally {
            setIsLoading(false);
        }
    }, [status, session?.accessToken]);

    // Fetch les plans au montage du composant
    useEffect(() => {
        fetchAvailablePlans();
    }, [fetchAvailablePlans]);

    // Fetch l'abonnement quand l'utilisateur est authentifié
    useEffect(() => {
        fetchSubscription();
    }, [fetchSubscription]);

    // Vérifier si l'utilisateur a un abonnement
    const hasSubscription = subscription?.stripe !== null && subscription?.local !== null;

    // Helpers utiles
    const isActive = hasSubscription &&
        (subscription?.stripe?.status === 'active' ||
            subscription?.stripe?.status === 'trialing');

    const isCanceled = hasSubscription && subscription?.stripe?.status === 'canceled';

    const willCancelAtPeriodEnd = subscription?.stripe?.cancel_at_period_end || false;

    const currentPeriodEnd = subscription?.stripe?.current_period_end
        ? new Date(subscription.stripe.current_period_end)
        : null;

    const tier = subscription?.subscription_product?.tier || null;

    const productName = subscription?.subscription_product?.name || null;

    const userRole = subscription?.user_role;

    // Vérifier si l'abonnement expire bientôt (moins de 7 jours)
    const isExpiringSoon = currentPeriodEnd
        ? (currentPeriodEnd - new Date()) < 7 * 24 * 60 * 60 * 1000
        : false;

    // Helper pour trouver un plan spécifique par slug ou tier
    const getPlanBySlug = useCallback((slug) => {
        return availablePlans.find(plan => plan.slug === slug || plan.tier === slug);
    }, [availablePlans]);

    // Helper pour obtenir le prix mensuel ou annuel d'un plan
    const getPlanPrice = useCallback((slug, isYearly = false) => {
        const plan = getPlanBySlug(slug);
        if (!plan) return null;
        return isYearly ? plan.stripe_yearly_price_id : plan.stripe_price_id;
    }, [getPlanBySlug]);

    return {
        // Données principales
        subscription,
        local: subscription?.local,
        stripe: subscription?.stripe,
        product: subscription?.subscription_product,

        // Plans disponibles
        availablePlans,

        // États
        isLoading,
        isLoadingPlans,
        error,
        plansError,
        isAuthenticated: status === 'authenticated',

        // Helpers
        hasSubscription,
        isActive,
        isCanceled,
        willCancelAtPeriodEnd,
        currentPeriodEnd,
        isExpiringSoon,
        tier,
        productName,
        userRole,
        getPlanBySlug,
        getPlanPrice,

        // Actions
        refetch: fetchSubscription,
        refetchPlans: fetchAvailablePlans,
    };
}

// Exemple d'utilisation dans un composant:
/*
function MyComponent() {
  const {
    subscription,
    availablePlans,
    isLoading,
    isLoadingPlans,
    error,
    hasSubscription,
    isActive,
    tier,
    productName,
    getPlanBySlug
  } = useSubscription();

  if (isLoading || isLoadingPlans) return <div>Chargement...</div>;
  if (error) return <div>Erreur: {error}</div>;

  // L'utilisateur n'a jamais souscrit ou a annulé
  if (!hasSubscription) {
    return (
      <div>
        <h2>Aucun abonnement</h2>
        <p>Souscrivez à un abonnement pour accéder aux fonctionnalités premium</p>

        <h3>Plans disponibles:</h3>
        {availablePlans.map(plan => (
          <div key={plan.id}>
            <h4>{plan.name}</h4>
            <p>Tier: {plan.tier}</p>
            <button>Souscrire</button>
          </div>
        ))}
      </div>
    );
  }

  // L'utilisateur a un abonnement mais il est inactif
  if (!isActive) {
    return (
      <div>
        <h2>Abonnement inactif</h2>
        <p>Votre abonnement a expiré</p>
      </div>
    );
  }

  // L'utilisateur a un abonnement actif
  const expertPlan = getPlanBySlug('cwf-expert');

  return (
    <div>
      <h2>Abonnement: {productName}</h2>
      <p>Tier: {tier}</p>
      <p>Expire le: {subscription.stripe.current_period_end}</p>

      {tier === 'cwf-starter' && expertPlan && (
        <div>
          <p>Passez au plan {expertPlan.name} pour plus de fonctionnalités !</p>
          <button>Upgrader</button>
        </div>
      )}
    </div>
  );
}
*/